import { getActiveLocale } from '../i18n';

/**
 * Locale-aware date and number formatting.
 *
 * These follow the *app's* language setting, not the browser's. Call sites used
 * to call `toLocaleDateString()` bare, so a user reading a German UI still got
 * `9/3/2026` if their browser was set to English.
 *
 * `Intl` formatters are relatively expensive to construct, and the library list
 * builds one per row, so they're memoised per locale.
 */

const BCP47 = { en: 'en-US', de: 'de-DE' } as const;

const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat>();

function memo<T extends Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat>(
  key: string,
  make: () => T,
): T {
  const hit = cache.get(key);
  if (hit) return hit as T;
  const made = make();
  cache.set(key, made);
  return made;
}

function tag(): string {
  return BCP47[getActiveLocale()];
}

/** Short calendar date, e.g. `03.09.2026` (de) or `9/3/2026` (en). */
export function formatDate(ms: number): string {
  return memo(`d:${tag()}`, () => new Intl.DateTimeFormat(tag(), { dateStyle: 'short' })).format(
    ms,
  );
}

/** Date + time, for tooltips and version history. */
export function formatDateTime(ms: number): string {
  return memo(
    `dt:${tag()}`,
    () => new Intl.DateTimeFormat(tag(), { dateStyle: 'medium', timeStyle: 'short' }),
  ).format(ms);
}

/** Grouped integer, e.g. `1.234` (de) or `1,234` (en). */
export function formatNumber(n: number): string {
  return memo(`n:${tag()}`, () => new Intl.NumberFormat(tag())).format(n);
}

const MINUTE = 60_000;
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * MINUTE],
  ['month', 30 * 24 * 60 * MINUTE],
  ['day', 24 * 60 * MINUTE],
  ['hour', 60 * MINUTE],
  ['minute', MINUTE],
];

/**
 * Coarse relative time ("vor 2 Stunden"), falling back to an absolute date past
 * a week — beyond that, "vor 3 Monaten" is less useful than the actual date.
 */
export function formatRelative(ms: number, now = Date.now()): string {
  const diff = ms - now;
  const abs = Math.abs(diff);
  if (abs > 7 * 24 * 60 * MINUTE) return formatDate(ms);
  if (abs < MINUTE) {
    return memo(`r:${tag()}`, () => new Intl.RelativeTimeFormat(tag(), { numeric: 'auto' })).format(
      0,
      'minute',
    );
  }
  for (const [unit, size] of UNITS) {
    if (abs >= size) {
      return memo(
        `r:${tag()}`,
        () => new Intl.RelativeTimeFormat(tag(), { numeric: 'auto' }),
      ).format(Math.round(diff / size), unit);
    }
  }
  return formatDate(ms);
}
